import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ExternalLink, Heart, Stamp, Facebook, Instagram, Linkedin, Twitter, Star } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const footerLinks = {
  services: [
    { label: "Individual Tax Prep", href: "/services#individual" },
    { label: "Business Tax", href: "/services#business" },
    { label: "Entity Formation", href: "/entity-formation" },
    { label: "Bookkeeping", href: "/bookkeeping" },
    { label: "Tax Planning", href: "/services#planning" },
    { label: "AI Automation", href: "/ai-automation" },
  ],
  company: [
    { label: "Start Your Return", href: "/start" },
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Resources", href: "/resources" },
    { label: "Client Portal", href: "/portal" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "Accessibility", href: "/accessibility" },
    { label: "SMS/Email Consent", href: "/consent" },
  ],
};

export default function Footer() {
  const settings = useSettings();
  const socials = [
    { url: settings.facebook_url, icon: Facebook, label: "Facebook" },
    { url: settings.instagram_url, icon: Instagram, label: "Instagram" },
    { url: settings.linkedin_url, icon: Linkedin, label: "LinkedIn" },
    { url: settings.twitter_url, icon: Twitter, label: "Twitter" },
  ].filter((s) => s.url);

  return (
    <footer className="bg-navy text-white noise-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_dataxguy-web/artifacts/lb9f7g04_884B7728-8B38-47E9-9302-1537F1F9BE36.png" 
              alt="DA TAXGUY Office"
              className="w-full max-w-[200px] rounded-lg mb-6 opacity-80"
            />
            <div className="space-y-4 text-gray-300">
              <a 
                href="tel:+12012537674" 
                className="flex items-center gap-3 hover:text-bronze transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5 text-bronze" />
                {settings.business_phone}
              </a>
              <a 
                href={`mailto:${settings.business_email}`}
                className="flex items-center gap-3 hover:text-bronze transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5 text-bronze" />
                {settings.business_email}
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-bronze flex-shrink-0 mt-1" />
                <span>New York, NY<br />Nationwide Virtual Services</span>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-bronze flex-shrink-0 mt-1" />
                <span>Mon-Fri: 9AM - 6PM<br />Sat: By Appointment</span>
              </div>
            </div>
            {socials.length > 0 && (
              <div className="flex items-center gap-3 mt-6">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-bronze hover:text-navy flex items-center justify-center transition-colors"
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                  >
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-body font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-bronze transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-body font-semibold text-lg mb-6">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-bronze transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Portal Access */}
          <div>
            <h4 className="font-body font-semibold text-lg mb-6">Client Access</h4>
            <div className="space-y-4">
              <a
                href={settings.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-bronze hover:bg-bronze/90 text-navy font-medium px-5 py-3 rounded-full transition-colors"
                data-testid="footer-portal-btn"
              >
                Secure Client Login
                <ExternalLink className="w-4 h-4" />
              </a>
              {settings.google_review_url && (
                <a
                  href={settings.google_review_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-300 hover:text-bronze transition-colors text-sm"
                  data-testid="footer-review-link"
                >
                  <Star className="w-4 h-4 text-bronze" />
                  Leave a Google Review
                </a>
              )}
            </div>

            <div className="mt-8">
              <h4 className="font-body font-semibold text-lg mb-4">Legal</h4>
              <ul className="space-y-2">
                {footerLinks.legal.map((link) => (
                  <li key={link.href}>
                    <Link 
                      to={link.href}
                      className="text-gray-400 text-sm hover:text-bronze transition-colors"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Nonprofit Services */}
            <div className="mt-8 pt-6 border-t border-gray-700 space-y-3">
              <Link
                to="/nonprofit"
                className="flex items-center gap-2 text-bronze hover:text-bronze/80 transition-colors"
                data-testid="footer-nonprofit-link"
              >
                <Heart className="w-4 h-4" />
                <span className="font-body text-sm">Non-Profit Services</span>
              </Link>
              <Link
                to="/notary"
                className="flex items-center gap-2 text-bronze hover:text-bronze/80 transition-colors"
                data-testid="footer-notary-link"
              >
                <Stamp className="w-4 h-4" />
                <span className="font-body text-sm">Notary Services</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-400 text-sm">
            <p>© {new Date().getFullYear()} DA TAXGUY & ASSOCIATES. All rights reserved.</p>
            <p className="text-center md:text-right max-w-xl">
              Information on this website is for general informational purposes and does not constitute tax, legal, or financial advice. Tax results depend on individual circumstances.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
